"use client";

import type { BlockProps } from "./blocks/registry";
import { Heading } from "../design-system/atoms/Heading";
import Icon from "../design-system/atoms/Icon";
import { Button } from "../design-system/atoms/Button";
import Link from "next/link";
import { Paragraph } from "../design-system/atoms/Paragraph";
import { resolveLegacyLink } from "../utils/linkUtils";
import { useMobileSlider } from "../hooks/useMobileSlider";

type StrapiMedia = {
  url: string;
};

type BienEtreCta = {
  text: string;
  link: string;
};

type TypeMenuCard = {
  id?: number | string;
  titre: string;
  descr: string;
  iconAlt?: string;
  icon: StrapiMedia;
  cta: BienEtreCta;
};

type BienEtreData = {
  id?: number | string;
  documentId?: string;
  titre: string;
  descr?: string;
  typeMenu: TypeMenuCard[];
};

export default function BienEtre({ data }: BlockProps<BienEtreData>) {
  const menus = data?.typeMenu ?? [];

  const { sliderRef, activeIndex, handleScroll, scrollToIndex } =
    useMobileSlider({
      itemsLength: menus.length,
    });

  if (!data) return null;

  return (
    <section
      className="py-16 lms-container bg-white overflow-hidden"
      key={data.id}
    >
      <div className="text-center mb-12">
        <Heading
          as="h2"
          className="lg:text-[44px] text-4xl font-bold text-text"
        >
          {data.titre.split(" ").slice(0, -1).join(" ")}{" "}
          <span className="text-secondary">
            {data.titre.split(" ").slice(-1)}
          </span>
        </Heading>

        {data.descr && (
          <Paragraph className="mt-4 text-lg text-text">
            {data.descr}
          </Paragraph>
        )}
      </div>

      {/* Slider mobile + grid desktop */}
      <div className="w-full max-w-full min-w-0 overflow-hidden md:overflow-visible">
        <div
          ref={sliderRef}
          onScroll={handleScroll}
          className="
            flex w-full max-w-full min-w-0 overflow-x-auto scroll-smooth snap-x snap-mandatory
            md:grid md:grid-cols-2 md:gap-8 md:overflow-visible md:snap-none
            lg:grid-cols-4
            max-w-7xl mx-auto justify-items-center
            [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          "
        >
          {menus.map((card, i) => (
            <div
              key={card.id ?? i}
              className="
                basis-full min-w-0 shrink-0 snap-center flex justify-center px-2
                md:basis-auto md:shrink md:snap-none md:px-0 md:w-full
              "
            >
              <article
                className="
                  w-full max-w-[320px]
                  flex h-full flex-col items-center rounded-2xl bg-white
                  px-6 py-10 text-center transition duration-300
                  border border-transparent
                  hover:border-gray-200 hover:shadow-[0px_8px_30px_rgba(0,0,0,0.1)]
                  md:max-w-none
                "
              >
                <Icon
                  src={card.icon.url}
                  alt={card.iconAlt ?? card.titre}
                  className="h-24 w-24 object-contain text-white"
                />

                <h3 className="mt-6 text-xl font-bold text-gray-800">
                  {card.titre}
                </h3>

                <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600">
                  {card.descr}
                </p>

                <Link href={resolveLegacyLink(card.cta.link)}>
                  <Button className="mt-6 w-fit rounded-3px border border-gray-300 bg-white px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-700 hover:bg-primaryGreen hover:text-white transition">
                    {card.cta.text}
                  </Button>
                </Link>
              </article>
            </div>
          ))}
        </div>
      </div>

      {/* Dots mobile */}
      {menus.length > 1 && (
        <div className="flex justify-center gap-2 mt-6 md:hidden">
          {menus.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === index
                  ? "w-6 bg-secondary"
                  : "w-2 bg-gray-300"
              }`}
              aria-label={`Aller au menu ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}